import { useState, useEffect, useContext, useRef, useLayoutEffect, useMemo } from "react"
import { MDBBtn, MDBCard, MDBCardBody, MDBIcon, MDBModal, MDBModalBody, MDBModalContent, MDBModalDialog, MDBModalFooter, MDBModalHeader, MDBModalTitle, MDBStepper, MDBStepperStep, MDBTypography } from "mdb-react-ui-kit"
import { useTranslation } from "react-i18next"
import { useSearchParams, Link, useLocation } from "react-router-dom";
import { generateClient } from 'aws-amplify/api';
import { UserContext } from "../../../App";
import getEventDays from "../../../utils/getEventDays";
import useDebounce from "../../../utils/debounce";
import EventAndTicket from "./book-ticket-components/EventAndTicket";
import Visit from "./book-ticket-components/Visit";
import PersonalInformation from "./book-ticket-components/PersonalInformation";
import Confirmation from "./book-ticket-components/Confirmation";
import Success from "./book-ticket-components/Success";
import StripeRedirectingOverlay from "../../../components-user/StripeRedirectingOverlay";
import { useIndustry } from "../../../utils/industryContextHook";
import { captureError } from '../../../utils/sentryHelpers';
import { fullAddressSchema, visitSchema } from "../../../validation/schemas/generic";
import { hotjar } from "react-hotjar";
import { fetchAuthSession } from "aws-amplify/auth";

// GraphQL Mutations for Ticket and Order creation
const CREATE_TICKET = /* GraphQL */ `
  mutation CreateTicket(
    $input: CreateTicketInput!
    $condition: ModelTicketConditionInput
  ) {
    createTicket(input: $input, condition: $condition) {
      id
      type
      invitationCode
      orderNumber
      billingEmail
      paymentStatus
      createdAt
      updatedAt
      eventDays
      userTicketsId
      ticketEventId
      ticketCompanyId
      ticketPaymentId
      __typename
    }
  }
`;

const DELETE_TICKET = /* GraphQL */ `
  mutation DeleteTicket($id: ID!) {
    deleteTicket(input: { id: $id }) {
      id
    }
  }
`;

const DELETE_ORDER = /* GraphQL */ `
  mutation DeleteOrder($id: ID!) {
    deleteOrder(input: { id: $id }) {
      id
    }
  }
`;

async function getIdToken() {
  const { idToken } = (await fetchAuthSession()).tokens ?? {};
  return idToken;
}

export default function BookTicket() {
  const { currentIndustry } = useIndustry();
  const { user } = useContext(UserContext);
  const client = generateClient();
  const { t } = useTranslation()
  const [hasExistingTicket, setHasExistingTicket] = useState(false);
  const [isCheckingTicket, setIsCheckingTicket] = useState(true);
  const [isCreatingTicket, setIsCreatingTicket] = useState(false);
  const [ticketId, setTicketId] = useState(null);
  const location = useLocation();
  const [event, setEvent] = useState(location.state?.event || null);

  // Stripe Checkout state
  const [isProcessingCheckout, setIsProcessingCheckout] = useState(false);
  const isIntentionalRedirect = useRef(false); // skip beforeunload on our own Stripe redirect
  const [checkoutError, setCheckoutError] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [eventDays, setEventDays] = useState([]);

  // URL parameters - read synchronously on first render
  const [routerParams, setRouterParams] = useSearchParams();
  const [returnedFromCancel] = useState(routerParams.get('cancelled') === 'true');

  const initialParams = useMemo(() => {
    const parsed = {
      ticketType: "default",
      selectedDays: [],
      vatInfo: { vatNumber: "" },
    };
    // Keys that are only used for Stripe return handling, not for booking params
    const stripReturnKeys = ['cancelled', 'ticketId', 'orderId', 'session_id'];
    for (const [key, value] of routerParams.entries()) {
      if (stripReturnKeys.includes(key)) continue;
      if (value === 'true') parsed[key] = true;
      else if (value === 'false') parsed[key] = false;
      else if (key === 'selectedDays' && value) parsed[key] = value.split(",").filter(day => day);
      else if (value) parsed[key] = value;
    }
    if (!parsed.selectedDays) parsed.selectedDays = [];
    return parsed;
  }, []);

  const [params, setParams] = useState(initialParams);
  const [isLoading, setIsLoading] = useState(!location.state?.event);
  const [isHandlingCancel, setIsHandlingCancel] = useState(returnedFromCancel);
  const [errors, setErrors] = useState([]);
  const [activeStep, setActiveStep] = useState(1);

  // Pending ticket state — when a ticket exists with unpaid status, skip booking and go straight to payment
  const [pendingTicket, setPendingTicket] = useState(null);

  const checkExistingTicket = async () => {
    if (!user?.id || !params.eventId) return;

    try {
      const ticketsResponse = await client.graphql({
        query: /* GraphQL */ `
        query GetUser($id: ID!, $ticketEventId: ID!) {
          getUser(id: $id) {
                id
                owner
                tickets(filter: {ticketEventId: {eq: $ticketEventId}}) {
                  items {
                    id
                    paymentStatus
                    ticketPaymentId
                  }
                }
                orders {
                  items {
                    id
                    financial { paymentProvider }
                    lineItems {
                      items {
                        bookedTicket { id }
                      }
                    }
                  }
                }
              }
          }
        `,
        variables: {
          id: user.id,
          ticketEventId: params.eventId
        }
      });
      const tickets = ticketsResponse.data.getUser.tickets.items;
      if (tickets.length > 0) {
        // Pending/retry-payment flow only applies to Stripe bookings. Decide per-ticket
        // via the linked order's paymentProvider (immutable), falling back to the event
        // config if the ticket has no order (legacy/free).
        const orders = ticketsResponse.data.getUser.orders?.items ?? [];
        const providerByTicketId = new Map();
        orders.forEach((order) => {
          const provider = order.financial?.paymentProvider;
          if (!provider) return;
          order.lineItems?.items?.forEach((li) => {
            const ticketId = li.bookedTicket?.id;
            if (ticketId) providerByTicketId.set(ticketId, provider);
          });
        });
        const unpaidTicket = tickets.find((candidate) => {
          const pending = candidate.paymentStatus === "unpaid" || candidate.paymentStatus === "pending";
          if (!pending) return false;
          const provider = providerByTicketId.get(candidate.id)
            ?? (event?.instantPaymentOnly === true ? "stripe" : "invoice");
          return provider === "stripe";
        });
        if (unpaidTicket) {
          setPendingTicket(unpaidTicket);
        } else {
          setHasExistingTicket(true);
        }
      }
    } catch (error) {
      console.error("Error checking existing tickets:", error);
      captureError(error, {
        feature: 'ticket-management',
        operation: 'check-existing-ticket',
        consoleMessage: 'Error checking existing tickets:',
        context: { userId: user?.id, eventId: params.eventId }
      });
    } finally {
      setIsCheckingTicket(false);
    }
  };

  // User selections
  const [taxRate, setTaxRate] = useState(19);
  const [customerType, setCustomerType] = useState("consumer");
  const [customerTypeSetByUser, setCustomerTypeSetByUser] = useState(false);
  const [personalAddress, setPersonalAddress] = useState({});
  const [personalContact, setPersonalContact] = useState({
    phone: "",
    email: ""
  });
  const [useProfileAddressForBilling, setUseProfileAddressForBilling] = useState(true);
  const [billingAddress, setBillingAddress] = useState({});

  // Detect Mobile Device
  const [isMobile, setIsMobile] = useState(false);

  useLayoutEffect(() => {
    const updateSize = () => {
      setIsMobile(window.innerWidth < 768);
    }
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  // Warn user before leaving during Stripe checkout redirect (but not on our own redirect)
  useEffect(() => {
    if (!isProcessingCheckout) return;
    const handleBeforeUnload = (e) => {
      if (isIntentionalRedirect.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isProcessingCheckout]);

  useEffect(() => {
    // Tickets: place of supply is always Germany, so 19% VAT always applies
    if ((personalAddress.company || billingAddress.company) && !customerTypeSetByUser) {
      setCustomerType("business");
    }
    setTaxRate(19);
  }, [customerType, personalAddress, billingAddress, useProfileAddressForBilling]);

  const stepperRef = useRef(null);

  const hasVisitStep = !!(event?.bookingSelectDays || event?.hasInvitationCodes);

  // Validation check for a given step
  const isStepValid = (step) => {
    switch (step) {
      case 1: // EventAndTicket
        return !!params.ticketType;
      case 2: // Visit (only if hasVisitStep, otherwise this is PersonalInformation)
        if (!hasVisitStep) {
          // Step 2 is PersonalInformation when no visit step
          return validations?.personalAddress?.length === 0 &&
            validations?.personalContact?.length === 0 &&
            (useProfileAddressForBilling || validations?.billingAddress?.length === 0);
        }
        // Visit step validation
        return (params.selectedDays?.length > 0) &&
          (!event?.invitationCodeRequired || !!params.invitationCode?.trim());
      case 3:
        if (hasVisitStep) {
          // Step 3 is PersonalInformation
          return validations?.personalAddress?.length === 0 &&
            validations?.personalContact?.length === 0 &&
            (useProfileAddressForBilling || validations?.billingAddress?.length === 0);
        }
        return true; // Confirmation step — always accessible if prior steps valid
      case 4:
        return true; // Confirmation step
      default:
        return true;
    }
  };

  // Check if all steps up to (but not including) targetStep are valid
  const canNavigateToStep = (targetStep) => {
    for (let i = 1; i < targetStep; i++) {
      if (!isStepValid(i)) return false;
    }
    return true;
  };

  const handleStepChange = (newStep) => {
    // After Stripe cancel return, all steps were already filled — allow free navigation
    if (returnedFromCancel || newStep <= activeStep || canNavigateToStep(newStep)) {
      setErrors([]);
      setActiveStep(newStep);
    }
  };

  const handleClickNext = () => {
    setActiveStep(prev => prev + 1);
  }

  const handleClickPrev = () => {
    setActiveStep(prev => prev > 1 ? prev - 1 : prev);
  }

  const goToStep = (step) => {
    setActiveStep(step);
  }

  useEffect(() => {
    const stepperEl = stepperRef.current;
    if (!stepperEl) return;

    let prevActiveIndex = -1;

    const observer = new MutationObserver(() => {
      const steps = stepperEl.querySelectorAll(".stepper-step");
      const activeIndex = Array.from(steps).findIndex(s => s.classList.contains("stepper-active"));
      if (activeIndex !== -1 && activeIndex !== prevActiveIndex) {
        prevActiveIndex = activeIndex;
        const mainEl = document.querySelector("main");
        if (mainEl) {
          mainEl.scrollTo({ top: 0, behavior: "smooth" });
        }
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    });

    observer.observe(stepperEl, { attributes: true, attributeFilter: ["class"], subtree: true });

    return () => observer.disconnect();
  }, [event]);

  useEffect(() => {
    if (user) {
      setPersonalAddress({
        salutation: user.salutation,
        title: user.title,
        firstName: user.firstName,
        lastName: user.lastName,
        street: user.contact?.address?.street || "",
        streetNumber: user.contact?.address?.streetNumber || "",
        zip: user.contact?.address?.zip || "",
        city: user.contact?.address?.city || "",
        countryCode: user.contact?.address?.countryCode || "",
        company: user.contact?.address?.company || ""
      });

      // Initialize personalContact with user data if available
      setPersonalContact({
        phone: user.contact?.phone || "",
        email: user.contact?.email || user.email || ""
      });
    }
  }, [user]);

  // URL parameters

  // Check for Stripe return (success or cancel)
  const cancelCleanupDone = useRef(false);
  useEffect(() => {
    const sessionId = routerParams.get('session_id');
    const cancelled = routerParams.get('cancelled');
    const ticketIdFromUrl = routerParams.get('ticketId');
    const orderIdFromUrl = routerParams.get('orderId');

    if (cancelled === 'true' && ticketIdFromUrl) {
      // Wait for event to be loaded before handling cancel return
      if (!event) return;
      // Guard: only run cleanup once
      if (cancelCleanupDone.current) return;
      cancelCleanupDone.current = true;

      const confirmationStep = (event.bookingSelectDays || event.hasInvitationCodes) ? 4 : 3;

      // User cancelled payment - keep ticket+order for retry, just store IDs
      sessionStorage.setItem("pendingBooking", JSON.stringify({
        ticketId: ticketIdFromUrl,
        orderId: orderIdFromUrl,
        eventId: params.eventId
      }));

      setHasExistingTicket(false);
      setErrors([{ type: 'cancel', message: t("Payment cancelled. You can try booking again.") }]);
      setShowValidations({ visit: false, personalAddress: false, personalContact: false, billingAddress: false });
      setActiveStep(confirmationStep);
      setIsHandlingCancel(false);
      // Clean URL — remove cancel keys
      const cleanParams = new URLSearchParams(routerParams);
      cleanParams.delete('cancelled');
      cleanParams.delete('ticketId');
      cleanParams.delete('orderId');
      setRouterParams(cleanParams);
    } else if (sessionId && ticketIdFromUrl) {
      // User returned from successful payment — clean up pending booking
      sessionStorage.removeItem("pendingBooking");
      setSessionId(sessionId);
      setTicketId(ticketIdFromUrl);
      if (orderIdFromUrl) setOrderId(orderIdFromUrl);
    }
  }, [routerParams, event]);

  // Update URL parameters when user selections change
  useEffect(() => {
    // Create a clean object with only the parameters we want to show in URL
    const urlParams = {};

    // Add ticketType
    if (params.ticketType) {
      urlParams.ticketType = params.ticketType;
    }

    // Only add selectedDays if there are any days selected
    if (params.selectedDays && params.selectedDays.length > 0) {
      urlParams.selectedDays = params.selectedDays.join(",");
    }

    // Add other necessary params but exclude complex objects like vatInfo
    if (params.eventId) urlParams.eventId = params.eventId;
    if (params.boothId) urlParams.boothId = params.boothId;
    if (params.invitationCode) urlParams.invitationCode = params.invitationCode;
    if (params.orderNumber) urlParams.orderNumber = params.orderNumber;
    if (params.companyId) urlParams.companyId = params.companyId;
    if (params.adminId) urlParams.adminId = params.adminId;

    // Never add complex objects like vatInfo to URL

    // Preserve cancel params until cleanup is done
    if (isHandlingCancel) {
      const tid = routerParams.get('ticketId');
      const oid = routerParams.get('orderId');
      if (tid) urlParams.ticketId = tid;
      if (oid) urlParams.orderId = oid;
      urlParams.cancelled = 'true';
    }

    setRouterParams(urlParams);
  }, [params]);

  useEffect(() => {
    if (!returnedFromCancel) {
      checkExistingTicket();
    }
  }, [user?.id, params.eventId]);

  // Fetch event
  const getEvent = async () => {
    if (params.eventId) {
      try {
        const eventData = await client.graphql({
          query: /* GraphQL */ `
              query GetEvent($id: ID!) {
                getEvent(id: $id) {
                  id
                  type
                  name {
                    language
                    text
                  }
                  description {
                    language
                    text
                  }
                  bookingNotice {
                    language
                    text
                  }
                  status
                  image {
                    fileName
                    alt
                  }
                  date
                  enableTicketBooking
                  enableStaffBooking
                  instantPaymentOnly
                  autoGenerateInvoice
                  paymentFlow
                  hasInvitationCodes
                  invitationCodeRequired
                  suggestedInvitationCode
                  bookingSelectDays
                  ticketPricePerDay
                  startDate
                  endDate
                  startTime
                  endTime
                  location {
                    name {
                      language
                      text
                      __typename
                    }
                    address {
                      street
                      street2
                      streetNumber
                      company
                      contactPerson
                      city
                      zip
                      countryCode
                      __typename
                    }
                    coordinates {
                      lat
                      lng
                      __typename
                    }
                    __typename
                  }
                  ticketProduct {
                    id
                    name {
                      language
                      text
                    }
                    price
                    image {
                      main {
                        fileName
                      }
                    }
                  }
                  badgePreview {
                    fileName
                  }
                  bookingTicketBulletPoints {
                    text {
                      language
                      text
                    }
                  }
                  floorPlan {
                    url
                    s3Path
                    fileName
                    type
                    name
                    __typename
                  }
                  landingPageUrl
                  ticketTAC {
                    language
                    text
                  }
                  ticketPrivacyPolicy {
                    language
                    text
                  }
                }
              }
            `,
          variables: { id: params.eventId }
        });

        setEvent(eventData.data.getEvent);
        setEventDays(getEventDays(eventData.data.getEvent));
        if (!eventData.data.getEvent.bookingSelectDays) {
          setParams(old => ({ ...old, selectedDays: getEventDays(eventData.data.getEvent) }));
        }
      } catch (error) {
        console.error(error);
        captureError(error, {
          feature: 'ticket-management',
          operation: 'fetch-event-details',
          consoleMessage: 'Error:',
          context: { eventId: params.eventId }
        });
      }
    }
  }
  useEffect(() => {
    getEvent().then(() => setIsLoading(false))
  }, [params.eventId]);


  const [orderDiscounts, setOrderDiscounts] = useState([]);
  const [linkedDiscount, setLinkedDiscount] = useState(null);

  // Create ticket for FREE events (no Stripe checkout needed)
  const createFreeTicket = async () => {
    if (!user.id) {
      hotjar.event("Book ticket without user data");
    }
    try {
      setIsCreatingTicket(true);
      const ticketData = await client.graphql({
        query: /* GraphQL */ `
        mutation CreateTicket(
          $input: CreateTicketInput!
          $condition: ModelTicketConditionInput
        ) {
          createTicket(input: $input, condition: $condition) {
            id
            type
            invitationCode
            orderNumber
            billingEmail
            createdAt
            updatedAt
            eventDays
            userTicketsId
            ticketEventId
            ticketCompanyId
            ticketPaymentId
            __typename
          }
        }
      `,
        variables: {
          input: {
            industryId: currentIndustry.id,
            type: params.ticketType,
            userTicketsId: user.id,
            createdByUserId: user.id,
            addons: params.createCompanyProfile ? ["company-profile"] : [],
            eventDays: [...(params.selectedDays || [])].sort(),
            ticketEventId: params.eventId,
            invitationCode: params.invitationCode,
            personalAddress: personalAddress,
            personalContact: personalContact,
            firstName: personalAddress.firstName,
            lastName: personalAddress.lastName,
            companyName: personalAddress.company,
            billingAddress: useProfileAddressForBilling ? null : billingAddress,
            isFreeTicket: true,
            visitorType: params.visitorType || null
          }
        }
      });
      setTicketId(ticketData.data.createTicket.id);
      setIsCreatingTicket(false);
      hotjar.event("Free ticket booked");
    } catch (error) {
      //console.error(error);
      captureError(error, {
        feature: 'ticket-management',
        operation: 'create-free-ticket',
        consoleMessage: 'Error:',
        context: {
          userId: user.id,
          eventId: params.eventId,
          ticketType: params.ticketType,
          selectedDays: params.selectedDays
        }
      });
      setErrors(old => [...old, { type: 'error', message: t("Unable to create ticket. Please contact support.") }]);
      setIsCreatingTicket(false);
      throw error;
    }
  }

  const updateUserPersonalAddress = async () => {
    try {
      const personalAddressForUser = { ...personalAddress };
      delete personalAddressForUser.salutation;
      delete personalAddressForUser.title;
      delete personalAddressForUser.firstName;
      delete personalAddressForUser.lastName;

    } catch (error) {
      captureError(error, {
        feature: 'ticket-management',
        operation: 'update-user-personal-address',
        consoleMessage: 'Error:',
        context: { userId: user.id }
      });
    }
  }

  const hasUserPersonalAddressChanged = () => {
    return user.contact?.address?.street !== personalAddress.street ||
      user.contact?.address?.streetNumber !== personalAddress.streetNumber ||
      user.contact?.address?.zip !== personalAddress.zip ||
      user.contact?.address?.city !== personalAddress.city ||
      user.contact?.address?.countryCode !== personalAddress.countryCode ||
      user.contact?.address?.company !== personalAddress.company ||
      user.contact?.phone !== personalContact.phone ||
      user.contact?.email !== personalContact.email
  }

  // Map field paths to translated labels for error messages
  const getFieldLabel = (fieldPath) => {
    const fieldLabels = {
      firstName: t("First Name"),
      lastName: t("Last name"),
      street: t("Street"),
      streetNumber: t("House Number"),
      zip: t("Postal Code"),
      city: t("City"),
      countryCode: t("Country"),
      company: t("Company"),
      title: t("Title"),
      phone: t("Phone"),
      email: t("Email"),
      invitationCode: t("Invitation code"),
      selectedDays: t("Days"),
    };
    return fieldLabels[fieldPath] || fieldPath;
  };

  const buildValidationErrors = (v) => {
    const vals = v || validations;
    const errorMessages = [];

    if (vals?.visit?.length > 0) {
      const fields = vals.visit.map(getFieldLabel).join(", ");
      errorMessages.push(`${t("Your visit")}: ${fields}`);
    }

    if (vals?.personalAddress?.length > 0) {
      const fields = vals.personalAddress.map(getFieldLabel).join(", ");
      errorMessages.push(`${t("Personal information")}: ${fields}`);
    }

    if (vals?.personalContact?.length > 0) {
      const fields = vals.personalContact.map(getFieldLabel).join(", ");
      errorMessages.push(`${t("Contact data")}: ${fields}`);
    }

    if (!useProfileAddressForBilling && vals?.billingAddress?.length > 0) {
      const fields = vals.billingAddress.map(getFieldLabel).join(", ");
      errorMessages.push(`${t("Billing Address")}: ${fields}`);
    }

    return errorMessages;
  };

  // Handle booking - either Stripe Checkout or direct free ticket creation
  const [bookingInProgress, setBookingInProgress] = useState(false);
  const handleBookTicket = async () => {
    // Variables for rollback tracking
    let newTicketId = null;
    let orderId = null;

    // Validate against CURRENT state, not debounced state
    const currentPersonalAddressErrors = fullAddressSchema.validate(personalAddress).map((v) => v.path);
    if (customerType === "business" && useProfileAddressForBilling && !personalAddress?.company?.trim()) {
      if (!currentPersonalAddressErrors.includes("company")) {
        currentPersonalAddressErrors.push("company");
      }
    }
    const currentPersonalContactErrors = [];
    const currentBillingAddressErrors = fullAddressSchema.validate(billingAddress).map((v) => v.path);
    if (customerType === "business" && !useProfileAddressForBilling && !billingAddress?.company?.trim()) {
      if (!currentBillingAddressErrors.includes("company")) {
        currentBillingAddressErrors.push("company");
      }
    }
    let currentVisitErrors = visitSchema.validate({ ...params }).map((v) => v.path);
    if (event?.invitationCodeRequired && !params.invitationCode?.trim()) {
      if (!currentVisitErrors.includes('invitationCode')) {
        currentVisitErrors.push('invitationCode');
      }
    }

    const currentValidations = {
      visit: currentVisitErrors,
      personalAddress: currentPersonalAddressErrors,
      personalContact: currentPersonalContactErrors,
      billingAddress: currentBillingAddressErrors
    };

    // Update validations state so UI reflects current errors
    setValidations(currentValidations);

    if (currentValidations.visit?.length > 0 || currentValidations.personalAddress?.length > 0 || currentValidations.personalContact?.length > 0 || (!useProfileAddressForBilling && currentValidations.billingAddress?.length > 0)) {
      setErrors(buildValidationErrors(currentValidations).map(msg => ({ type: 'field', message: msg })));
      return;
    }

    if (!currentIndustry?.id) {
      setErrors([{ type: 'error', message: t("Unable to create ticket. Please try again.") }]);
      console.error("currentIndustry is null in handleBookTicket", { userId: user?.id, eventId: params?.eventId });
      return;
    }

    try {
      setBookingInProgress(true);
      setCheckoutError(null);

      // Sort selected days chronologically before sending to backend
      const sortedSelectedDays = [...(params.selectedDays || [])].sort();

      if (hasUserPersonalAddressChanged()) {
        updateUserPersonalAddress();
      }

      // Check if ticket is FREE (price is 0 or null, or fully discounted by codes)
      const effectiveQty = event.ticketPricePerDay ? (sortedSelectedDays?.length || 1) : 1;
      const basePrice = (event?.ticketProduct?.price || 0) * effectiveQty;
      const totalDiscount = orderDiscounts.reduce((sum, d) => sum + (d.amount || 0), 0);
      const effectivePrice = basePrice - totalDiscount;
      const isFreeTicket = !event?.ticketProduct?.price || event.ticketProduct.price === 0 || effectivePrice <= 0;

      if (isFreeTicket) {
        // Free ticket - create directly without Stripe
        await createFreeTicket();
      } else if (event?.instantPaymentOnly === true) {
        // Paid ticket flow: Create Ticket → Create Order → Open Stripe Checkout
        setIsProcessingCheckout(true);

        const idToken = await getIdToken();
        const GRAPHQL_ENDPOINT = "https://y3vayhw2i5bqrm6fpwqz7fzfwe.appsync-api.eu-central-1.amazonaws.com/graphql";

        // Check for existing pending booking (retry after cancel/refresh)
        const pending = JSON.parse(sessionStorage.getItem("pendingBooking") ?? "null");
        const hasPendingBooking = pending?.eventId === params.eventId && pending?.ticketId && pending?.orderId;

        if (hasPendingBooking) {
          // RETRY: Reuse existing ticket+order, skip directly to Stripe
          newTicketId = pending.ticketId;
          orderId = pending.orderId;
        } else {
          // FRESH BOOKING: Create ticket + order first

          // STEP 1: Create Ticket with paymentStatus: "pending"
          const ticketData = await client.graphql({
            query: CREATE_TICKET,
            variables: {
              input: {
                industryId: currentIndustry.id,
                type: params.ticketType,
                userTicketsId: user.id,
                createdByUserId: user.id,
                addons: params.createCompanyProfile ? ["company-profile"] : [],
                eventDays: sortedSelectedDays,
                ticketEventId: params.eventId,
                invitationCode: params.invitationCode,
                personalAddress: personalAddress,
                personalContact: personalContact,
                firstName: personalAddress.firstName,
                lastName: personalAddress.lastName,
                companyName: personalAddress.company,
                billingAddress: useProfileAddressForBilling ? null : billingAddress,
                visitorType: params.visitorType || null,
                //paymentStatus: "pending" // Key field for pending payment
              }
            }
          });

          const createdTicket = ticketData.data.createTicket;
          newTicketId = createdTicket.id;

          // STEP 2: Place Order with paymentProvider: "stripe"
          const effectiveQty = event.ticketPricePerDay ? (sortedSelectedDays?.length || 1) : 1;
          const netAmount = event.ticketProduct.price * effectiveQty;

          const daysNote = sortedSelectedDays?.length
            ? `Gebuchte Tage: ${sortedSelectedDays.map(d => new Date(d + "T00:00:00").toLocaleDateString("de-DE")).join(", ")}`
            : "";

          const orderInput = {
            type: "ticket",
            industryOrdersId: currentIndustry.id,
            isAdminOrder: false,
            notes: daysNote,
            deliveryAddress: personalAddress,
            billingAddress: useProfileAddressForBilling ? personalAddress : billingAddress,
            billingEmail: personalContact.email,
            products: [{
              id: event.ticketProduct.id,
              ticketId: newTicketId,
              qty: effectiveQty,
              discount: 0,
              discountType: "",
              taxRate: taxRate
            }],
            isBusiness: customerType === "business",
            vatInfo: params.vatInfo || null,
            currency: "EUR",
            totalAmount: netAmount * (1 + taxRate / 100),
            invoiceAmount: netAmount * (1 + taxRate / 100),
            invoiceAmountNet: netAmount,
            taxRate: taxRate,
            paymentProvider: "stripe",
            data: JSON.stringify({
              eventId: params.eventId,
              ticketId: newTicketId,
              ticketType: params.ticketType,
              selectedDays: sortedSelectedDays,
              discounts: orderDiscounts.length > 0 ? orderDiscounts : []
            })
          };

          const orderResponse = await fetch(GRAPHQL_ENDPOINT, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': idToken
            },
            body: JSON.stringify({
              query: `mutation PlaceOrder($order: String) {
                placeOrder(order: $order)
              }`,
              variables: {
                order: JSON.stringify(orderInput)
              }
            })
          });

          const orderResponseData = await orderResponse.json();
          if (orderResponseData.errors) {
            throw new Error(orderResponseData.errors[0].message);
          }
          orderId = orderResponseData.data.placeOrder;

          // Persist for retry in case user cancels or page refreshes
          sessionStorage.setItem("pendingBooking", JSON.stringify({
            ticketId: newTicketId,
            orderId: orderId,
            eventId: params.eventId
          }));
        }

        // STEP 3: Create Stripe Checkout Session and redirect to Stripe
        const baseUrl = `${window.location.origin}/book-ticket`;
        const successUrl = `${baseUrl}?ticketId=${newTicketId}&orderId=${orderId}&session_id={CHECKOUT_SESSION_ID}`; // Stripe replaces {CHECKOUT_SESSION_ID}
        const selectedDaysParam = sortedSelectedDays?.length ? `&selectedDays=${sortedSelectedDays.join(",")}` : "";
        const invitationCodeParam = params.invitationCode ? `&invitationCode=${params.invitationCode}` : "";
        const cancelUrl = `${baseUrl}?cancelled=true&ticketId=${newTicketId}&orderId=${orderId}&eventId=${params.eventId}&ticketType=${params.ticketType}${selectedDaysParam}${invitationCodeParam}`;

        const checkoutResponse = await fetch(GRAPHQL_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': idToken
          },
          body: JSON.stringify({
            query: `mutation CreateStripeCheckoutSession($orderId: String!, $successUrl: String!, $cancelUrl: String!) {
              createStripeCheckoutSession(orderId: $orderId, successUrl: $successUrl, cancelUrl: $cancelUrl)
            }`,
            variables: {
              orderId: orderId,
              successUrl: successUrl,
              cancelUrl: cancelUrl
            }
          })
        });

        const checkoutResponseData = await checkoutResponse.json();

        if (checkoutResponseData.errors && checkoutResponseData.errors.length > 0) {
          throw new Error(checkoutResponseData.errors[0].message ?? "Failed to create Stripe checkout session");
        }

        if (!checkoutResponseData.data.createStripeCheckoutSession) {
          throw new Error("No response data from createStripeCheckoutSession");
        }

        const checkoutResult = JSON.parse(checkoutResponseData.data.createStripeCheckoutSession);

        if (checkoutResult.sessionUrl) {
          hotjar.event("Stripe checkout redirect initiated");
          isIntentionalRedirect.current = true;
          window.location.href = checkoutResult.sessionUrl;
        } else {
          throw new Error("No session URL received from server");
        }
      } else {
        // Paid ticket with invoice payment - create ticket and place order (no Stripe checkout)
        // Do NOT set isProcessingCheckout here: that flag gates the "redirected to payment" overlay,
        // which must never appear in the invoice flow. bookingInProgress already drives the button spinner.

        // STEP 1: Create Ticket
        const ticketData = await client.graphql({
          query: CREATE_TICKET,
          variables: {
            input: {
              industryId: currentIndustry.id,
              type: params.ticketType,
              userTicketsId: user.id,
              createdByUserId: user.id,
              addons: params.createCompanyProfile ? ["company-profile"] : [],
              eventDays: sortedSelectedDays,
              ticketEventId: params.eventId,
              invitationCode: params.invitationCode,
              personalAddress: personalAddress,
              personalContact: personalContact,
              firstName: personalAddress.firstName,
              lastName: personalAddress.lastName,
              companyName: personalAddress.company,
              billingAddress: useProfileAddressForBilling ? null : billingAddress,
              visitorType: params.visitorType || null
            }
          }
        });

        const createdTicket = ticketData.data.createTicket;
        newTicketId = createdTicket.id;

        // STEP 2: Place Order with paymentProvider: "invoice"
        const idToken = await getIdToken();

        const effectiveQtyInvoice = event.ticketPricePerDay ? (sortedSelectedDays?.length || 1) : 1;
        const netAmountInvoice = event.ticketProduct.price * effectiveQtyInvoice;

        const daysNoteInvoice = sortedSelectedDays?.length
          ? `Gebuchte Tage: ${sortedSelectedDays.map(d => new Date(d + "T00:00:00").toLocaleDateString("de-DE")).join(", ")}`
          : "";

        const orderInput = {
          type: "ticket",
          industryOrdersId: currentIndustry.id,
          isAdminOrder: false,
          notes: daysNoteInvoice,
          deliveryAddress: personalAddress,
          billingAddress: useProfileAddressForBilling ? personalAddress : billingAddress,
          billingEmail: personalContact.email,
          products: [{
            id: event.ticketProduct.id,
            ticketId: newTicketId,
            qty: effectiveQtyInvoice,
            discount: 0,
            discountType: "",
            taxRate: taxRate
          }],
          isBusiness: customerType === "business",
          vatInfo: params.vatInfo || null,
          currency: "EUR",
          totalAmount: netAmountInvoice * (1 + taxRate / 100),
          invoiceAmount: netAmountInvoice * (1 + taxRate / 100),
          invoiceAmountNet: netAmountInvoice,
          taxRate: taxRate,
          paymentProvider: "invoice",
          data: JSON.stringify({
            eventId: params.eventId,
            ticketId: newTicketId,
            ticketType: params.ticketType,
            selectedDays: sortedSelectedDays,
            discounts: orderDiscounts.length > 0 ? orderDiscounts : []
          })
        };

        const GRAPHQL_ENDPOINT = "https://y3vayhw2i5bqrm6fpwqz7fzfwe.appsync-api.eu-central-1.amazonaws.com/graphql";

        const orderResponse = await fetch(GRAPHQL_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': idToken
          },
          body: JSON.stringify({
            query: `mutation PlaceOrder($order: String) {
              placeOrder(order: $order)
            }`,
            variables: {
              order: JSON.stringify(orderInput)
            }
          })
        });

        const orderResponseData = await orderResponse.json();
        if (orderResponseData.errors) {
          throw new Error(orderResponseData.errors[0].message);
        }
        orderId = orderResponseData.data.placeOrder;

        setTicketId(newTicketId);
        setOrderId(orderId);
        hotjar.event("Invoice ticket booked");
        setTimeout(() => {
          setBookingInProgress(false);
        }, 1000);
      }

    } catch (error) {
      // ROLLBACK: Only delete ticket+order if this was a fresh booking (not a retry)
      const wasRetry = !!JSON.parse(sessionStorage.getItem("pendingBooking") ?? "null")?.ticketId;
      if (!wasRetry) {
        try {
          if (newTicketId) {
            await client.graphql({
              query: DELETE_TICKET,
              variables: { id: newTicketId }
            });
          }

          if (orderId) {
            await client.graphql({
              query: DELETE_ORDER,
              variables: { id: orderId }
            });
          }
          sessionStorage.removeItem("pendingBooking");
        } catch (rollbackError) {
          captureError(rollbackError, {
            feature: 'ticket-management',
            operation: 'rollback-on-booking-error',
            consoleMessage: 'Rollback error:',
            context: {
              ticketId: newTicketId,
              orderId: orderId
            }
          });
        }
      }

      const errorMessage = event?.ticketProduct?.price > 0
        ? t("Unable to initiate payment. Please try again or contact support.")
        : t("Unable to create ticket. Please contact support.");

      hotjar.event(event?.ticketProduct?.price > 0 ? "Error creating Stripe checkout" : "Error creating free ticket");
      captureError(error, {
        feature: 'ticket-management',
        operation: event?.ticketProduct?.price > 0 ? 'create-stripe-checkout' : 'create-free-ticket',
        consoleMessage: 'Error:',
        context: {
          userId: user?.id,
          eventId: params.eventId,
          ticketType: params.ticketType,
          ticketPrice: event?.ticketProduct?.price
        }
      });
      setCheckoutError(errorMessage);
      setErrors(old => [...old, { type: 'error', message: errorMessage }]);
      setBookingInProgress(false);
      setIsProcessingCheckout(false);
    }
  }

  // Retry payment for an existing pending ticket — finds the associated order and creates a new Stripe session
  const handleRetryPayment = async (ticket) => {
    try {
      setIsProcessingCheckout(true);
      setCheckoutError(null);

      const idToken = await getIdToken();
      const GRAPHQL_ENDPOINT = "https://y3vayhw2i5bqrm6fpwqz7fzfwe.appsync-api.eu-central-1.amazonaws.com/graphql";

      // Check sessionStorage first for a known orderId
      const pending = JSON.parse(sessionStorage.getItem("pendingBooking") ?? "null");
      let retryOrderId = pending?.eventId === params.eventId ? pending?.orderId : null;

      // If no orderId in sessionStorage, find the order linked to this ticket via API
      if (!retryOrderId) {
        const ordersResponse = await client.graphql({
          query: /* GraphQL */ `
            query GetUser($id: ID!) {
              getUser(id: $id) {
                id
                owner
                orders {
                  items {
                    id
                    paymentStatus
                    lineItems {
                      items {
                        bookedTicket { id }
                      }
                    }
                  }
                }
              }
            }
          `,
          variables: { id: user.id }
        });

        const matchingOrder = ordersResponse.data.getUser.orders.items.find(order =>
          order.lineItems?.items?.some(li => li.bookedTicket?.id === ticket.id)
        );

        if (!matchingOrder) {
          throw new Error("No order found for this ticket");
        }
        retryOrderId = matchingOrder.id;
      }

      // Save for future retries
      sessionStorage.setItem("pendingBooking", JSON.stringify({
        ticketId: ticket.id,
        orderId: retryOrderId,
        eventId: params.eventId
      }));

      // Create new Stripe Checkout Session
      const baseUrl = `${window.location.origin}/book-ticket`;
      const successUrl = `${baseUrl}?ticketId=${ticket.id}&orderId=${retryOrderId}&session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${baseUrl}?cancelled=true&ticketId=${ticket.id}&orderId=${retryOrderId}&eventId=${params.eventId}&ticketType=${params.ticketType}`;

      const checkoutResponse = await fetch(GRAPHQL_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': idToken
        },
        body: JSON.stringify({
          query: `mutation CreateStripeCheckoutSession($orderId: String!, $successUrl: String!, $cancelUrl: String!) {
            createStripeCheckoutSession(orderId: $orderId, successUrl: $successUrl, cancelUrl: $cancelUrl)
          }`,
          variables: {
            orderId: retryOrderId,
            successUrl: successUrl,
            cancelUrl: cancelUrl
          }
        })
      });

      const checkoutResponseData = await checkoutResponse.json();

      if (checkoutResponseData.errors?.length > 0) {
        throw new Error(checkoutResponseData.errors[0].message ?? "Failed to create Stripe checkout session");
      }

      if (!checkoutResponseData.data.createStripeCheckoutSession) {
        throw new Error("No response data from createStripeCheckoutSession");
      }

      const checkoutResult = JSON.parse(checkoutResponseData.data.createStripeCheckoutSession);

      if (checkoutResult.sessionUrl) {
        hotjar.event("Stripe checkout retry initiated");
        isIntentionalRedirect.current = true;
        window.location.href = checkoutResult.sessionUrl;
      } else {
        throw new Error("No session URL received from server");
      }
    } catch (error) {
      captureError(error, {
        feature: 'ticket-management',
        operation: 'retry-stripe-checkout',
        consoleMessage: 'Retry payment error:',
        context: { ticketId: ticket.id, eventId: params.eventId }
      });
      setCheckoutError(t("Unable to initiate payment. Please try again or contact support."));
      setIsProcessingCheckout(false);
    }
  };

  // Cancel a pending (unpaid) ticket and its associated order
  const [isCancellingTicket, setIsCancellingTicket] = useState(false);
  const [cancelTicketConfirm, setCancelTicketConfirm] = useState(null); // ticket object or null

  const requestCancelPendingTicket = (ticket) => setCancelTicketConfirm(ticket);
  const closeCancelConfirm = () => {
    if (!isCancellingTicket) setCancelTicketConfirm(null);
  };

  const handleCancelPendingTicket = async (ticket) => {
    try {
      setIsCancellingTicket(true);

      // Find the associated order
      const pending = JSON.parse(sessionStorage.getItem("pendingBooking") ?? "null");
      let cancelOrderId = pending?.ticketId === ticket.id ? pending?.orderId : null;

      if (!cancelOrderId) {
        const ordersResponse = await client.graphql({
          query: /* GraphQL */ `
            query GetUser($id: ID!) {
              getUser(id: $id) {
                id
                owner
                orders {
                  items {
                    id
                    lineItems {
                      items {
                        bookedTicket { id }
                      }
                    }
                  }
                }
              }
            }
          `,
          variables: { id: user.id }
        });
        const matchingOrder = ordersResponse.data.getUser.orders.items.find(order =>
          order.lineItems?.items?.some(li => li.bookedTicket?.id === ticket.id)
        );
        cancelOrderId = matchingOrder?.id;
      }

      // Delete ticket
      await client.graphql({
        query: DELETE_TICKET,
        variables: { id: ticket.id }
      });

      // Delete order if found
      if (cancelOrderId) {
        await client.graphql({
          query: DELETE_ORDER,
          variables: { id: cancelOrderId }
        });
      }

      sessionStorage.removeItem("pendingBooking");
      setPendingTicket(null);
      setHasExistingTicket(false);
    } catch (error) {
      captureError(error, {
        feature: 'ticket-management',
        operation: 'cancel-pending-ticket',
        consoleMessage: 'Error cancelling pending ticket:',
        context: { ticketId: ticket.id }
      });
      setCheckoutError(t("Unable to cancel ticket. Please try again or contact support."));
    } finally {
      setIsCancellingTicket(false);
    }
  };

  // Validation
  const [validations, setValidations] = useState({});
  const [showValidations, setShowValidations] = useState({
    visit: false,
    personalAddress: false,
    personalContact: false,
    billingAddress: false
  });

  const debouncedPersonalAddress = useDebounce(personalAddress, 1000);
  const debouncedPersonalContact = useDebounce(personalContact, 1000);
  const debouncedBillingAddress = useDebounce(billingAddress, 1000);
  const debouncedParams = useDebounce(params, 500);

  useEffect(() => {
    const personalProfile = fullAddressSchema.validate(debouncedPersonalAddress).map((validation) => validation.path);
    if (customerType === "business" && useProfileAddressForBilling && !debouncedPersonalAddress?.company?.trim()) {
      if (!personalProfile.includes("company")) {
        personalProfile.push("company");
      }
    }
    const personalContactErrors = [];
    // Add validation for contact data
    // Email validation is already handled by the input type="email"

    // Visit validation - add invitationCode as required if event.invitationCodeRequired is true
    let visitErrors = visitSchema.validate({ ...debouncedParams }).map((validation) => validation.path);
    if (event?.invitationCodeRequired && !debouncedParams.invitationCode?.trim()) {
      if (!visitErrors.includes('invitationCode')) {
        visitErrors.push('invitationCode');
      }
    }

    const billingErrors = fullAddressSchema.validate(debouncedBillingAddress).map((validation) => validation.path);
    if (customerType === "business" && !useProfileAddressForBilling && !debouncedBillingAddress?.company?.trim()) {
      if (!billingErrors.includes("company")) {
        billingErrors.push("company");
      }
    }

    setValidations({
      personalAddress: personalProfile,
      personalContact: personalContactErrors,
      billingAddress: billingErrors,
      visit: visitErrors
    });
  }, [debouncedPersonalAddress, debouncedPersonalContact, debouncedBillingAddress, debouncedParams, event?.invitationCodeRequired, customerType, useProfileAddressForBilling]);

  // No modal handlers needed for hosted checkout - user is redirected to Stripe

  return (
    <>
      {/* Confirmation modal for cancelling a pending ticket — replaces window.confirm */}
      <MDBModal open={!!cancelTicketConfirm} onClose={closeCancelConfirm} tabIndex="-1" staticBackdrop={isCancellingTicket}>
        <MDBModalDialog centered>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>{t("Cancel ticket?")}</MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={closeCancelConfirm} disabled={isCancellingTicket} />
            </MDBModalHeader>
            <MDBModalBody>
              {t("Are you sure you want to cancel this ticket? This action cannot be undone.")}
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn color="secondary" outline onClick={closeCancelConfirm} disabled={isCancellingTicket}>
                {t("Keep ticket")}
              </MDBBtn>
              <MDBBtn
                color="danger"
                disabled={isCancellingTicket}
                onClick={async () => {
                  const ticketToCancel = cancelTicketConfirm;
                  if (!ticketToCancel) return;
                  await handleCancelPendingTicket(ticketToCancel);
                  setCancelTicketConfirm(null);
                }}
              >
                {isCancellingTicket
                  ? <><span className="spinner-border spinner-border-sm me-2" />{t("Cancelling...")}</>
                  : <><MDBIcon fas icon="times" className="me-2" />{t("Cancel ticket")}</>
                }
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>

      <StripeRedirectingOverlay show={isProcessingCheckout} />
      {/* Show loading while handling Stripe cancel return */}
      {isHandlingCancel ?
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t("Loading")}...</span>
          </div>
        </div>
      :
      /* Show Success component if we have a ticketId OR sessionId from Stripe */
      (ticketId || sessionId) ?
        <Success ticketId={ticketId} sessionId={sessionId} orderId={orderId} event={event} /> :
        pendingTicket ?
          (<MDBCard className="mt-4">
            <MDBCardBody className="text-center">
              <MDBIcon fas icon="credit-card" className="text-warning mb-3" size="3x" />
              <h5 className="fw-bold">{t("You have a ticket with a pending payment")}</h5>
              <p className="text-muted mt-2">
                {t("Your ticket has been created but payment is still outstanding. Complete your payment to activate your ticket.")}
              </p>
              <div className="d-flex justify-content-center gap-3 mt-2">
                <MDBBtn
                  color="success"
                  disabled={isProcessingCheckout || isCancellingTicket}
                  onClick={() => handleRetryPayment(pendingTicket)}
                >
                  {isProcessingCheckout
                    ? <><span className="spinner-border spinner-border-sm me-2" />{t("Redirecting...")}</>
                    : <><MDBIcon fas icon="credit-card" className="me-2" />{t("Pay now")}</>
                  }
                </MDBBtn>
                <MDBBtn
                  color="danger"
                  outline
                  disabled={isProcessingCheckout || isCancellingTicket}
                  onClick={() => requestCancelPendingTicket(pendingTicket)}
                >
                  {isCancellingTicket
                    ? <><span className="spinner-border spinner-border-sm me-2" />{t("Cancelling...")}</>
                    : <><MDBIcon fas icon="times" className="me-2" />{t("Cancel ticket")}</>
                  }
                </MDBBtn>
              </div>
              {checkoutError && (
                <div className="alert alert-danger mt-3">{checkoutError}</div>
              )}
            </MDBCardBody>
          </MDBCard>)
          :
        hasExistingTicket ?
          (<MDBCard className="mt-4">
            <MDBCardBody>
              <MDBIcon fas icon="info-circle" className="me-2" />
              <strong>{t("You already have a ticket for this event")}</strong>
              <p className="mt-3">
                {t("Each person must book their ticket through their own account. Only registered companies can book tickets for all employees.")}
              </p>
            </MDBCardBody>
          </MDBCard>)
          :
          event && !event?.enableTicketBooking ?
            (
              <MDBCard className="mt-4">
                <MDBCardBody>
                  <MDBIcon fas icon="info-circle" className="me-2" />
                  <strong>{t("Ticket booking for this event has been disabled.")}</strong>
                  <p className="mt-3">
                    {t("You can purchase a ticket at the event entrance.")}
                  </p>
                </MDBCardBody>
              </MDBCard>
            )

            :
            !isLoading ?
              <>
                <MDBTypography tag='h1' className="text-center">{t("Book ticket")}</MDBTypography>
                {!event ? (
                  <MDBCard className="mt-4">
                    <MDBCardBody className="text-center">
                      <MDBIcon fas icon="exclamation-triangle" className="me-2" />
                      <strong>{t("No event selected")}</strong>
                      <p className="mt-3">
                        {t("Please select an event to book a ticket.")}
                      </p>
                      <Link to="/shop" className="btn btn-primary">
                        {t("Go to event overview")}
                      </Link>
                    </MDBCardBody>
                  </MDBCard>
                ) : (
                  <MDBCard className="mt-lg-4 shadow-0 border-0 border-lg">
                    <MDBCardBody className="p-1 p-sm-2 p-lg-4">
                      {event &&
                        <div ref={stepperRef}>
                          <MDBStepper activeStep={activeStep} onChange={handleStepChange} type="horizontal" className="text-center">
                            {/* Force all steps clickable after Stripe cancel return */}
                            {returnedFromCancel && <style>{`.stepper-step .stepper-head { pointer-events: auto !important; cursor: pointer !important; opacity: 1 !important; }`}</style>}
                            <MDBStepperStep itemId={1} headText={!isMobile && t("Select event and ticket")} headIcon={<MDBIcon fas icon="calendar-alt" />} >
                              <EventAndTicket
                                event={event}
                                params={params}
                                setParams={setParams}
                                handleClickPrev={handleClickPrev}
                                handleClickNext={handleClickNext}
                              />
                            </MDBStepperStep>
                            {(event.bookingSelectDays || event.hasInvitationCodes) &&
                              <MDBStepperStep itemId={2} headText={!isMobile && t("Your visit")} headIcon={<MDBIcon fas icon="calendar-day" />}>
                                <Visit
                                  params={params}
                                  setParams={setParams}
                                  eventDays={eventDays}
                                  event={event}
                                  hasInvitationCodes={event?.hasInvitationCodes}
                                  invitationCodeRequired={event?.invitationCodeRequired}
                                  suggestedInvitationCode={event?.suggestedInvitationCode}
                                  bookingSelectDays={event?.bookingSelectDays}
                                  handleClickPrev={handleClickPrev}
                                  handleClickNext={handleClickNext}
                                  validations={validations}
                                  showValidations={showValidations}
                                  setShowValidations={setShowValidations}
                                  setLinkedDiscount={setLinkedDiscount}
                                />
                              </MDBStepperStep>
                            }
                            <MDBStepperStep itemId={(event.bookingSelectDays || event.hasInvitationCodes) ? 3 : 2} headText={!isMobile && t("Personal information")} headIcon={<MDBIcon fas icon="user" />}>
                              <PersonalInformation
                                user={user}
                                event={event}
                                params={params}
                                setParams={setParams}
                                useProfileAddressForBilling={useProfileAddressForBilling}
                                setUseProfileAddressForBilling={setUseProfileAddressForBilling}
                                billingAddress={billingAddress}
                                setBillingAddress={setBillingAddress}
                                personalAddress={personalAddress}
                                setPersonalAddress={setPersonalAddress}
                                personalContact={personalContact}
                                setPersonalContact={setPersonalContact}
                                customerType={customerType}
                                setCustomerType={setCustomerType}
                                setCustomerTypeSetByUser={setCustomerTypeSetByUser}
                                handleClickPrev={handleClickPrev}
                                handleClickNext={handleClickNext}
                                validations={validations}
                                showValidations={showValidations}
                                setShowValidations={setShowValidations}
                              />
                            </MDBStepperStep>
                            <MDBStepperStep itemId={(event.bookingSelectDays || event.hasInvitationCodes) ? 4 : 3} headText={!isMobile && t("Confirmation")} headIcon={<MDBIcon fas icon="check" />}>
                              <Confirmation
                                user={user}
                                event={event}
                                eventId={params.eventId}
                                params={params}
                                taxRate={taxRate}
                                personalAddress={personalAddress}
                                personalContact={personalContact}
                                useProfileAddressForBilling={useProfileAddressForBilling}
                                setUseProfileAddressForBilling={setUseProfileAddressForBilling}
                                billingAddress={billingAddress}
                                setBillingAddress={setBillingAddress}
                                handleBookTicket={handleBookTicket}
                                bookingInProgress={bookingInProgress}
                                isProcessingCheckout={isProcessingCheckout}
                                handleClickPrev={handleClickPrev}
                                handleClickNext={handleClickNext}
                                goToStep={goToStep}
                                hasVisitStep={!!(event.bookingSelectDays || event.hasInvitationCodes)}
                                validations={validations}
                                showValidations={showValidations}
                                setShowValidations={setShowValidations}
                                setOrderDiscounts={setOrderDiscounts}
                                linkedDiscount={linkedDiscount}
                                errors={errors}
                              />
                            </MDBStepperStep>
                          </MDBStepper>
                        </div>
                      }
                    </MDBCardBody>
                  </MDBCard>
                )}
              </> :
              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">{t("Loading")}...</span>
                </div>
              </div>
      }
    </>
  )
}